import type { DiscountDefinitionInput, DiscountDefinitionView, OutletSummary } from "@kopi/contracts";
import { DEFAULT_DISCOUNT_SORT, DISCOUNT_SORTS } from "@kopi/contracts/client";
import {
  DISCOUNT_SCOPES,
  type DiscountScope,
  SALES_CHANNELS,
  type SalesChannel,
} from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Checkbox } from "@kopi/ui/components/checkbox";
import { DataTable, type DataTableColumn, type DataTableRowAction } from "@kopi/ui/components/data-table";
import { DatePicker } from "@kopi/ui/components/date-picker";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Switch } from "@kopi/ui/components/switch";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PencilIcon, PlusIcon, PowerIcon, PowerOffIcon, ShieldCheckIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useMemo, useState } from "react";
import { Page, PageHeader } from "@/components/page-header";
import { discountValueText, percentText } from "@/discounts/labels";
import { discountPolicyQuery, discountsKey, discountsQuery } from "@/discounts/queries";
import { discounts } from "@/i18n/messages/discounts";
import { type AreaTranslate, useMessages } from "@/i18n/provider";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { CHANNEL_KEYS } from "@/lib/channels";
import { type StatusFilter, statusOptions, statusToActive } from "@/lib/filters";
import { basisPointsInput, parseBasisPoints } from "@/lib/percent";
import { outletsQuery } from "@/lib/queries";
import { sortSearch, urlSorting } from "@/lib/sorting";

/** The column sort lives in the URL (R1-09d), so it survives a reload. */
const validateSearch = sortSearch(DISCOUNT_SORTS);

export const Route = createFileRoute("/_authed/discounts")({
  staticData: { section: "workspace", crumb: "nav.discounts" },
  validateSearch,
  loaderDeps: ({ search }) => ({ sort: search.sort }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(discountsQuery({ q: "", sort: deps.sort })),
      context.queryClient.ensureQueryData(outletsQuery),
    ]),
  component: DiscountsPage,
});

type T = AreaTranslate<typeof discounts.id>;
type ScopeFilter = "all" | DiscountScope;
type Dialog =
  | { kind: "create" }
  | { kind: "edit"; definition: DiscountDefinitionView }
  | { kind: "policy" }
  | null;

const SCOPE_KEYS = { line: "discounts.scope.line", order: "discounts.scope.order" } as const;

/** Whole rupiah typed by hand: digits only, no leading zeros. */
const digits = (value: string) => value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

/** "YYYY-MM-DD" and "HH:MM" of an instant on this device's clock. */
function localParts(iso: string | null): { day: string; time: string } {
  if (!iso) return { day: "", time: "" };
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    day: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
}

/** A device-local day and time as an ISO instant (a date-time without offset parses as local time). */
const instant = (day: string, time: string) =>
  day ? new Date(`${day}T${time || "00:00"}`).toISOString() : null;

function errorMessage(t: T, error: unknown): string {
  if (!(error instanceof ApiRequestError)) return t("common.saveOffline");
  switch (error.code) {
    case "validation_failed":
      return t("discounts.invalid");
    case "discount_revision_conflict":
      return t("discounts.conflict");
    case "discount_policy_revision_conflict":
      return t("discounts.policyConflict");
    case "not_found":
      return t("discounts.notFound");
    case "forbidden":
      return t("discounts.forbidden");
    default:
      return t("common.saveFailed");
  }
}

const inputOf = ({
  id: _id,
  revision: _revision,
  createdAt: _created,
  updatedAt: _updated,
  ...input
}: DiscountDefinitionView): DiscountDefinitionInput => input;

/** The owner's discount catalogue (R1-17): definitions and each outlet's cashier approval limit. */
function DiscountsPage() {
  const { t } = useMessages(discounts);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [scope, setScope] = useState<ScopeFilter>("all");
  const [dialog, setDialog] = useState<Dialog>(null);
  const { sort } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sorting = urlSorting(DISCOUNT_SORTS, DEFAULT_DISCOUNT_SORT, sort, (next) => {
    void navigate({ search: next ? { sort: next } : {}, replace: true });
  });
  const list = useInfiniteQuery(
    discountsQuery({ q, active: statusToActive(status), scope: scope === "all" ? undefined : scope, sort }),
  );
  const columns = useMemo(() => discountColumns(t), [t]);

  const toggle = useMutation({
    mutationFn: async (definition: DiscountDefinitionView) =>
      unwrap(
        await api.v1.discounts({ discountId: definition.id }).put({
          ...inputOf(definition),
          active: !definition.active,
          expectedRevision: definition.revision,
        }),
      ),
    onSuccess: async (_, definition) => {
      await queryClient.invalidateQueries({ queryKey: discountsKey });
      toast.success(
        t(definition.active ? "discounts.deactivated" : "discounts.activated", { name: definition.name }),
      );
    },
    onError: (error) => toast.error(errorMessage(t, error)),
  });

  const rowActions = (definition: DiscountDefinitionView): DataTableRowAction[] => [
    {
      label: t("discounts.edit"),
      icon: <PencilIcon />,
      onSelect: () => setDialog({ kind: "edit", definition }),
    },
    {
      label: t(definition.active ? "common.deactivate" : "common.activate"),
      icon: definition.active ? <PowerOffIcon /> : <PowerIcon />,
      disabled: toggle.isPending,
      onSelect: () => toggle.mutate(definition),
    },
  ];

  return (
    <Page>
      <PageHeader
        title={t("nav.discounts")}
        subtitle={t("discounts.subtitle")}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => setDialog({ kind: "policy" })}>
              <ShieldCheckIcon />
              {t("discounts.policy")}
            </Button>
            <Button type="button" onClick={() => setDialog({ kind: "create" })}>
              <PlusIcon />
              {t("discounts.add")}
            </Button>
          </>
        }
      />
      <DataTable
        paging="pages"
        label={t("discounts.list")}
        columns={columns}
        query={list}
        getRowId={(definition) => definition.id}
        search={{ value: q, onChange: setQ, placeholder: t("discounts.search") }}
        {...sorting}
        actions={
          sort && (
            <Button type="button" variant="outline" onClick={() => sorting.onSortingChange([])}>
              {t("discounts.newestFirst")}
            </Button>
          )
        }
        filters={
          <>
            <OptionSelect
              aria-label={t("discounts.scope")}
              className="md:w-48"
              options={[
                { value: "all" as ScopeFilter, label: t("discounts.scope.all") },
                ...DISCOUNT_SCOPES.map((s) => ({ value: s as ScopeFilter, label: t(SCOPE_KEYS[s]) })),
              ]}
              value={scope}
              onValueChange={setScope}
            />
            <OptionSelect
              aria-label={t("common.status")}
              className="md:w-44"
              options={statusOptions(t)}
              value={status}
              onValueChange={setStatus}
            />
          </>
        }
        rowActions={rowActions}
        empty={{
          title: t(
            q || status !== "all" || scope !== "all"
              ? "discounts.emptyFilteredTitle"
              : "discounts.emptyTitle",
          ),
          description: t("discounts.emptyHint"),
        }}
      />
      {(dialog?.kind === "create" || dialog?.kind === "edit") && (
        <DefinitionDialog
          outlets={outlets}
          definition={dialog.kind === "edit" ? dialog.definition : null}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "policy" && <PolicyDialog outlets={outlets} onClose={() => setDialog(null)} />}
    </Page>
  );
}

function ValueCell(props: { definition: DiscountDefinitionView }) {
  const { t, format, locale } = useMessages(discounts);
  const { definition } = props;
  return (
    <div className="flex min-w-0 flex-col">
      <span className="tabular-nums">{discountValueText(definition.value, format, locale)}</span>
      {definition.maxAmount !== null && (
        <span className="text-muted-foreground text-sm">
          {t("discounts.max", { amount: format.money(BigInt(definition.maxAmount)) })}
        </span>
      )}
    </div>
  );
}

function PeriodCell(props: { definition: DiscountDefinitionView }) {
  const { t, format } = useMessages(discounts);
  const { startsAt, endsAt } = props.definition;
  const text =
    startsAt && endsAt
      ? t("discounts.range", { start: format.dateTime(startsAt), end: format.dateTime(endsAt) })
      : startsAt
        ? t("discounts.from", { time: format.dateTime(startsAt) })
        : endsAt
          ? t("discounts.until", { time: format.dateTime(endsAt) })
          : t("discounts.always");
  return <span className="text-sm">{text}</span>;
}

function discountColumns(t: T): DataTableColumn<DiscountDefinitionView>[] {
  return [
    {
      id: "name",
      header: t("common.name"),
      enableSorting: true,
      meta: { card: "title" },
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium">{row.original.name}</span>
          <span className="truncate text-muted-foreground text-sm">
            {t(SCOPE_KEYS[row.original.scope])} ·{" "}
            {row.original.channels.map((channel) => t(CHANNEL_KEYS[channel])).join(", ")}
          </span>
        </div>
      ),
    },
    {
      id: "value",
      header: t("discounts.value"),
      cell: ({ row }) => <ValueCell definition={row.original} />,
    },
    {
      id: "period",
      header: t("discounts.period"),
      cell: ({ row }) => <PeriodCell definition={row.original} />,
    },
    {
      id: "outlets",
      header: t("discounts.outlets"),
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {t("discounts.outletCount", { count: row.original.outletIds.length })}
        </span>
      ),
    },
    {
      id: "status",
      header: t("common.status"),
      enableSorting: true,
      meta: { className: "md:w-32" },
      cell: ({ row }) =>
        row.original.active ? (
          <Badge variant="success">{t("common.active")}</Badge>
        ) : (
          <Badge variant="outline">{t("common.inactive")}</Badge>
        ),
    },
  ];
}

function Field(props: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={props.id}>{props.label}</Label>
      {props.children}
      {props.hint && <p className="text-muted-foreground text-sm">{props.hint}</p>}
    </div>
  );
}

/** Create or edit (full replacement with the revision it was opened at). Mounted only while open. */
function DefinitionDialog(props: {
  outlets: OutletSummary[];
  definition: DiscountDefinitionView | null;
  onClose: () => void;
}) {
  const { definition, outlets } = props;
  const { t, format, locale } = useMessages(discounts);
  const queryClient = useQueryClient();
  const start = localParts(definition?.startsAt ?? null);
  const end = localParts(definition?.endsAt ?? null);
  const [name, setName] = useState(definition?.name ?? "");
  const [scope, setScope] = useState<DiscountScope>(definition?.scope ?? "order");
  const [kind, setKind] = useState<"percent" | "fixed">(definition?.value.kind ?? "percent");
  const [percent, setPercent] = useState(
    definition?.value.kind === "percent" ? basisPointsInput(definition.value.basisPoints) : "",
  );
  const [amount, setAmount] = useState(definition?.value.kind === "fixed" ? definition.value.amount : "");
  const [maxAmount, setMaxAmount] = useState(definition?.maxAmount ?? "");
  const [channels, setChannels] = useState<SalesChannel[]>(definition?.channels ?? [...SALES_CHANNELS]);
  const [outletIds, setOutletIds] = useState<string[]>(
    definition?.outletIds ?? outlets.filter((o) => o.active).map((o) => o.id),
  );
  const [startDay, setStartDay] = useState(start.day);
  const [startTime, setStartTime] = useState(start.time || "00:00");
  const [endDay, setEndDay] = useState(end.day);
  const [endTime, setEndTime] = useState(end.time || "23:59");
  const [active, setActive] = useState(definition?.active ?? true);
  const ids = {
    name: useId(),
    scope: useId(),
    kind: useId(),
    value: useId(),
    max: useId(),
    start: useId(),
    startLabel: useId(),
    startTime: useId(),
    end: useId(),
    endLabel: useId(),
    endTime: useId(),
    active: useId(),
    period: useId(),
  };

  const points = parseBasisPoints(percent);
  const value: DiscountDefinitionInput["value"] | null =
    kind === "percent"
      ? points !== null && points > 0
        ? { kind, basisPoints: points }
        : null
      : amount && BigInt(amount) > 0n
        ? { kind, amount }
        : null;
  const startsAt = instant(startDay, startTime);
  const endsAt = instant(endDay, endTime);
  const periodInvalid = startsAt !== null && endsAt !== null && Date.parse(endsAt) <= Date.parse(startsAt);
  const incomplete =
    !name.trim() || !value || channels.length === 0 || outletIds.length === 0 || periodInvalid;

  const save = useMutation({
    mutationFn: async () => {
      if (!value) throw new Error("incomplete");
      const body: DiscountDefinitionInput = {
        name: name.trim(),
        scope,
        value,
        maxAmount: maxAmount ? maxAmount : null,
        active,
        outletIds,
        channels,
        startsAt,
        endsAt,
      };
      return definition
        ? unwrap(
            await api.v1
              .discounts({ discountId: definition.id })
              .put({ ...body, expectedRevision: definition.revision }),
          )
        : unwrap(await api.v1.discounts.post(body));
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: discountsKey });
      toast.success(t(definition ? "discounts.saved" : "discounts.created", { name: saved.name }));
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!save.isPending && !incomplete) save.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={definition ? t("discounts.editTitle", { name: definition.name }) : t("discounts.add")}
      description={t("discounts.formHint")}
      dismissible={!save.isPending}
      className="md:max-w-xl"
      footer={
        <Button type="submit" form="discount-definition" disabled={save.isPending || incomplete}>
          {t(save.isPending ? "common.saving" : "common.save")}
        </Button>
      }
    >
      <form id="discount-definition" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Field id={ids.name} label={t("discounts.name")}>
          <Input
            id={ids.name}
            value={name}
            maxLength={80}
            placeholder={t("discounts.namePlaceholder")}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field id={ids.scope} label={t("discounts.scope")}>
            <OptionSelect
              id={ids.scope}
              options={DISCOUNT_SCOPES.map((s) => ({ value: s, label: t(SCOPE_KEYS[s]) }))}
              value={scope}
              onValueChange={setScope}
            />
          </Field>
          <Field id={ids.kind} label={t("discounts.kind")}>
            <OptionSelect
              id={ids.kind}
              options={[
                { value: "percent" as const, label: t("discounts.kind.percent") },
                { value: "fixed" as const, label: t("discounts.kind.fixed") },
              ]}
              value={kind}
              onValueChange={setKind}
            />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {kind === "percent" ? (
            <Field id={ids.value} label={t("discounts.percent")}>
              <Input
                id={ids.value}
                inputMode="decimal"
                value={percent}
                aria-invalid={percent !== "" && points === null}
                onChange={(e) => setPercent(e.target.value)}
              />
            </Field>
          ) : (
            <Field
              id={ids.value}
              label={t("discounts.amount")}
              {...(scope === "line" ? { hint: t("discounts.fixedHint") } : {})}
            >
              <Input
                id={ids.value}
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(digits(e.target.value))}
              />
              {amount && (
                <span className="text-muted-foreground text-sm">
                  {t("discounts.amountPreview", { amount: format.money(BigInt(amount)) })}
                </span>
              )}
            </Field>
          )}
          <Field id={ids.max} label={t("discounts.maxAmount")} hint={t("discounts.maxHint")}>
            <Input
              id={ids.max}
              inputMode="numeric"
              value={maxAmount}
              onChange={(e) => setMaxAmount(digits(e.target.value))}
            />
          </Field>
        </div>
        {kind === "percent" && points !== null && points > 0 && (
          <p className="text-muted-foreground text-sm">
            {t("discounts.amountPreview", { amount: percentText(points, locale) })}
          </p>
        )}
        <fieldset className="flex flex-col gap-1">
          <legend className="font-medium text-sm">{t("discounts.channels")}</legend>
          <div className="flex flex-wrap gap-x-4">
            {SALES_CHANNELS.map((channel) => (
              <Label key={channel} className="min-h-11 font-normal">
                <Checkbox
                  checked={channels.includes(channel)}
                  onCheckedChange={(checked) =>
                    setChannels((current) =>
                      checked === true
                        ? SALES_CHANNELS.filter((c) => c === channel || current.includes(c))
                        : current.filter((c) => c !== channel),
                    )
                  }
                />
                {t(CHANNEL_KEYS[channel])}
              </Label>
            ))}
          </div>
        </fieldset>
        {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §4 exception). */}
        <fieldset className="flex flex-col gap-1">
          <legend className="font-medium text-sm">{t("discounts.outlets")}</legend>
          <p className="text-muted-foreground text-sm">{t("discounts.outletsHint")}</p>
          <div className="flex flex-col">
            {outlets.map((outlet) => (
              <Label key={outlet.id} className="min-h-11 font-normal">
                <Checkbox
                  checked={outletIds.includes(outlet.id)}
                  onCheckedChange={(checked) =>
                    setOutletIds((current) =>
                      checked === true
                        ? [...current.filter((id) => id !== outlet.id), outlet.id]
                        : current.filter((id) => id !== outlet.id),
                    )
                  }
                />
                {outlet.name}
              </Label>
            ))}
          </div>
        </fieldset>
        <fieldset className="flex flex-col gap-2" aria-describedby={ids.period}>
          <legend className="font-medium text-sm">{t("discounts.period")}</legend>
          <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
            <div className="flex flex-col gap-1.5">
              <Label id={ids.startLabel} htmlFor={ids.start}>
                {t("discounts.startsAt")}
              </Label>
              <DatePicker
                id={ids.start}
                labelId={ids.startLabel}
                value={startDay}
                onChange={setStartDay}
                locale={locale}
              />
            </div>
            <Field id={ids.startTime} label={t("discounts.startTime")}>
              <Input
                id={ids.startTime}
                type="time"
                value={startTime}
                disabled={!startDay}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </Field>
            <div className="flex flex-col gap-1.5">
              <Label id={ids.endLabel} htmlFor={ids.end}>
                {t("discounts.endsAt")}
              </Label>
              <DatePicker
                id={ids.end}
                labelId={ids.endLabel}
                value={endDay}
                onChange={setEndDay}
                locale={locale}
              />
            </div>
            <Field id={ids.endTime} label={t("discounts.endTime")}>
              <Input
                id={ids.endTime}
                type="time"
                value={endTime}
                disabled={!endDay}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </Field>
          </div>
          <p
            id={ids.period}
            className={periodInvalid ? "text-destructive text-sm" : "text-muted-foreground text-sm"}
          >
            {t(periodInvalid ? "discounts.endBeforeStart" : "discounts.periodHint")}
          </p>
        </fieldset>
        <Label htmlFor={ids.active} className="min-h-11 justify-between font-normal">
          {t("discounts.activeLabel")}
          <Switch id={ids.active} checked={active} onCheckedChange={setActive} />
        </Label>
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {errorMessage(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}

/** Each outlet's cashier threshold: discounts above this share of the gross subtotal need approval. */
function PolicyDialog(props: { outlets: OutletSummary[]; onClose: () => void }) {
  const { outlets } = props;
  const { t, locale } = useMessages(discounts);
  const queryClient = useQueryClient();
  const [outletId, setOutletId] = useState(outlets[0]?.id ?? "");
  const policy = useQuery(discountPolicyQuery(outletId));
  // Starts from the loaded policy; typing overrides it until another outlet is chosen.
  const [typed, setTyped] = useState<{ outletId: string; value: string } | null>(null);
  const text =
    typed?.outletId === outletId
      ? typed.value
      : policy.data
        ? basisPointsInput(policy.data.cashierThresholdBasisPoints)
        : "";
  const points = parseBasisPoints(text);
  const outletSelect = useId();
  const thresholdId = useId();
  const outletName = outlets.find((o) => o.id === outletId)?.name ?? "";

  const save = useMutation({
    mutationFn: async () => {
      if (points === null || !policy.data) throw new Error("incomplete");
      return unwrap(
        await api.v1.outlets({ outletId })["discount-policy"].put({
          cashierThresholdBasisPoints: points,
          expectedRevision: policy.data.revision,
        }),
      );
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: [...discountsKey, "policy", outletId] });
      toast.success(
        t("discounts.policySaved", {
          outlet: outletName,
          limit: percentText(saved.cashierThresholdBasisPoints, locale),
        }),
      );
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!save.isPending && points !== null && policy.data) save.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("discounts.policyTitle")}
      description={t("discounts.policyHint")}
      dismissible={!save.isPending}
      footer={
        <Button
          type="submit"
          form="discount-policy"
          disabled={save.isPending || points === null || !policy.data}
        >
          {t(save.isPending ? "common.saving" : "common.save")}
        </Button>
      }
    >
      <form id="discount-policy" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Field id={outletSelect} label={t("discounts.policyOutlet")}>
          {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
          <OptionSelect
            id={outletSelect}
            options={outlets.map((o) => ({ value: o.id, label: o.name }))}
            value={outletId}
            onValueChange={(next) => {
              setOutletId(next);
              setTyped(null);
            }}
          />
        </Field>
        <Field
          id={thresholdId}
          label={t("discounts.threshold")}
          {...(policy.data
            ? {
                hint: t("discounts.thresholdNow", {
                  limit: percentText(policy.data.cashierThresholdBasisPoints, locale),
                }),
              }
            : {})}
        >
          <Input
            id={thresholdId}
            inputMode="decimal"
            value={text}
            disabled={!policy.data}
            aria-invalid={text !== "" && points === null}
            onChange={(e) => setTyped({ outletId, value: e.target.value })}
          />
        </Field>
        {policy.isError && (
          <p role="alert" className="text-destructive text-sm">
            {t("discounts.policyLoadFailed")}
          </p>
        )}
        {text !== "" && points === null && (
          <p role="alert" className="text-destructive text-sm">
            {t("discounts.policyInvalid")}
          </p>
        )}
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {errorMessage(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
